import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router'
import { errorMessage, get, oauthStartUrl, post } from '../api'
import { pendingInvite, useAuth } from '../auth'
import { Alert, Button, Field, Input } from '../components/ui'
import type { Providers } from '../types'

const OAUTH_ERRORS: Record<string, string> = {
  oauth_state: 'The sign-in attempt did not complete. Please try again.',
  oauth_exchange: 'The provider did not accept the sign-in. Please try again.',
  provider_unavailable: 'That sign-in option is not set up yet.',
  email_taken: 'An account with that email already exists. Sign in with your password first, then link the provider from your account page.',
  no_email: 'The provider did not share an email address, which is needed to find your invitations.',
}

export function SignInPage() {
  const { me, loading, refresh } = useAuth()
  const [params] = useSearchParams()
  const returnTo = params.get('returnTo') ?? '/'
  const [providers, setProviders] = useState<Providers | null>(null)
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [remember, setRemember] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(params.get('error') ? (OAUTH_ERRORS[params.get('error')!] ?? 'Sign-in failed. Please try again.') : null)

  useEffect(() => {
    get<Providers>('/v1/auth/providers')
      .then(setProviders)
      .catch(() => setProviders({ providers: [], password: true }))
  }, [])

  if (!loading && me) return <Navigate to={returnTo} replace />

  const invite = pendingInvite.get() ?? undefined

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      if (mode === 'register') await post('/v1/auth/password/register', { email, password, name: name || undefined, remember, invite })
      else await post('/v1/auth/password/login', { email, password, remember })
      if (invite) pendingInvite.clear()
      await refresh()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <Link to="/" className="brand">
          <span className="brand-mark" /> Tabula
        </Link>
        <h1 style={{ marginBottom: 6 }}>{mode === 'register' ? 'Create your account' : 'Sign in'}</h1>
        <p className="muted small" style={{ marginBottom: 18 }}>
          {invite ? 'You have been invited to join a restaurant team. Sign in or create an account with the invited email.' : 'For restaurant staff and owners.'}
        </p>
        {error ? (
          <div style={{ marginBottom: 14 }}>
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {providers && providers.providers.length > 0 ? (
          <div className="stack">
            {providers.providers.includes('google') ? (
              <a className="provider-btn" href={oauthStartUrl('google', { returnTo, remember, invite })}>
                <KeyIcon /> Continue with Google
              </a>
            ) : null}
            {providers.providers.includes('apple') ? (
              <a className="provider-btn" href={oauthStartUrl('apple', { returnTo, remember, invite })}>
                <KeyIcon /> Continue with Apple
              </a>
            ) : null}
            {providers.providers.includes('microsoft') ? (
              <a className="provider-btn" href={oauthStartUrl('microsoft', { returnTo, remember, invite })}>
                <KeyIcon /> Continue with Microsoft
              </a>
            ) : null}
          </div>
        ) : null}
        {providers && providers.providers.length > 0 ? <div className="divider">or with email</div> : null}

        <form onSubmit={submit} className="stack">
          {mode === 'register' ? (
            <Field label="Your name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />}</Field>
          ) : null}
          <Field label="Email">{(id) => <Input id={id} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />}</Field>
          <Field label="Password" hint={mode === 'register' ? 'At least 10 characters. A short sentence works well.' : undefined}>
            {(id) => <Input id={id} type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={mode === 'register' ? 10 : 1} required />}
          </Field>
          <label className="row-flex small" style={{ cursor: 'pointer' }}>
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} /> Keep me signed in on this device for 30 days
          </label>
          <Button type="submit" variant="primary" size="lg" busy={busy}>
            {mode === 'register' ? 'Create account' : 'Sign in'}
          </Button>
        </form>
        <p className="small muted" style={{ marginTop: 16, textAlign: 'center' }}>
          {mode === 'register' ? (
            <>
              Already have an account?{' '}
              <button className="link-btn" onClick={() => setMode('login')}>
                Sign in
              </button>
            </>
          ) : (
            <>
              New here?{' '}
              <button className="link-btn" onClick={() => setMode('register')}>
                Create an account
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  )
}

/** A neutral glyph for provider buttons. Each provider's official button artwork can replace it. */
function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="12" r="4" />
      <path d="M12 12h9M18 12v3M21 12v2" />
    </svg>
  )
}
